/**
 * Find My Size. Size recommender for the Blister Hood tee.
 *
 * The algorithm and the custom element share one file on purpose. Horizon resolves
 * cross-asset imports through the `@theme/*` importmap in snippets/scripts.liquid, so a
 * second asset would mean either editing that shared snippet (wider blast radius, and the
 * edit is lost on theme updates) or relying on a relative CDN import. The algorithm below
 * touches no DOM and is exported for tests; the element is registered only in a browser.
 *
 * Nothing here makes a network request, and no answer is persisted anywhere.
 */

/* ------------------------------------------------------------------ *
 * Config. The whole tunable surface. Retuning after real exchange
 * data should never mean touching the logic below it.
 * ------------------------------------------------------------------ */

export const CONFIG = {
  sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'],

  /**
   * Half-open upper bounds in pounds, ascending. A weight under bands[i] (and at or over
   * bands[i-1]) maps to sizes[i]; anything at or over the last bound takes the final size.
   * Half-open so no weight can land between two bands.
   *
   * The 291 boundary splitting 3XL from 4XL is an educated guess, like the rest of this list.
   * It continues the widening trend of the bands below it (26, 30, 30, 35, 40) and leaves every
   * existing band untouched, so nobody who was getting 3XL yesterday moves.
   */
  weightBands: [130, 156, 186, 216, 251, 291],

  /**
   * Same shape as the weight bands, but these are not a guess: they are the vendor's published
   * body-chest chart, in inches.
   *
   * That chart leaves a hole between every band (36-37, 38-40, then 42-44) and the rule is that
   * a measurement in a hole takes the larger size. Encoding the bands as the boundaries *after*
   * the holes makes that structural rather than a special case: 41 is simply the bottom of the
   * L band, 45 the bottom of XL, and so on. Seven bands, one per size, no value unclaimed.
   */
  chestBands: [38, 41, 45, 49, 53, 57],

  tallInches: 74, // 6'2" and up, where the body length starts running short on this cut.
  shortInches: 64, // 5'4" and under

  /**
   * Two choices only, and neither sizes down. A shirt that arrives too big still gets worn; one
   * that arrives too small is a guaranteed return, so the tool errs generous on purpose.
   *
   * Known trade-off, currently unmitigated: the vendor fact sheet says women often size down one
   * or two on this unisex blank, and nothing in the tool offers a size below its own answer. The
   * only downward movement is the automatic height rule at 5'4" and under. A shopper who wants
   * smaller has to pick it from the product page swatches themselves. Revisit if exchange data
   * shows too-big becoming the common complaint.
   */
  fitOffsets: { designed: 0, roomy: 1 },

  limits: {
    weightLb: [60, 500],
    heightIn: [58, 80], // 4'10" - 6'8"
    chestIn: [30, 70],
  },

  messages: {
    selectFailed: 'Tap the size above to add it to your cart.',

    // Shown when the inputs point past the largest size we sell. `{size}` is replaced with that
    // size. Says it about the garment, not about the shopper, and does not block the sale: the
    // honest move is to let them decide with the fact in front of them.
    beyondLargest:
      '{size} is the largest this tee comes in, so it may fit closer than the relaxed cut intends.',
  },
};

/* ------------------------------------------------------------------ *
 * Unit conversion. Imperial is the source of truth: the weight bands
 * are in pounds and the height thresholds in inches, so storing metric
 * would put rounding drift right on the 74in / 64in boundaries.
 * ------------------------------------------------------------------ */

const LB_PER_KG = 2.2046226218;

export const cmToInches = (cm) => cm / 2.54;
export const inchesToCm = (inches) => inches * 2.54;
export const kgToLb = (kg) => kg * LB_PER_KG;
export const lbToKg = (lb) => lb / LB_PER_KG;

/** @param {number} inches @returns {{feet: number, inches: number}} */
export function inchesToFeetIn(inches) {
  const total = Math.round(inches);
  return { feet: Math.floor(total / 12), inches: total % 12 };
}

export const feetInToInches = (feet, inches) => feet * 12 + inches;


/* ------------------------------------------------------------------ *
 * The algorithm: weight sets the base size, height and fit shift it,
 * then the result is clamped to what we actually sell.
 * ------------------------------------------------------------------ */

/**
 * Position of a value in a list of half-open upper bounds. Anything at or above the last bound
 * takes the final size.
 * @param {number} value
 * @param {number[]} bands
 * @returns {number} index into CONFIG.sizes
 */
function bandIndex(value, bands) {
  const band = bands.findIndex((upper) => value < upper);
  return band === -1 ? CONFIG.sizes.length - 1 : band;
}

/**
 * @param {number} lb
 * @returns {number} index into CONFIG.sizes
 */
export function baseIndexFromWeight(lb) {
  return bandIndex(lb, CONFIG.weightBands);
}

/**
 * @param {number} inches Body chest, around the fullest part
 * @returns {number} index into CONFIG.sizes
 */
export function baseIndexFromChest(inches) {
  return bandIndex(inches, CONFIG.chestBands);
}

/** @param {number} inches @returns {-1 | 0 | 1} */
export function heightOffset(inches) {
  if (inches >= CONFIG.tallInches) return 1;
  if (inches <= CONFIG.shortInches) return -1;
  return 0;
}

/** @param {string} fit @returns {-1 | 0 | 1} */
export function fitOffset(fit) {
  return CONFIG.fitOffsets[fit] ?? 0;
}

const clampIndex = (i) => Math.min(Math.max(i, 0), CONFIG.sizes.length - 1);

/**
 * Positions in CONFIG.sizes that a given product actually carries, ascending.
 * Falls back to the full range when the caller passes nothing usable.
 * @param {string[]} [available]
 * @returns {number[]}
 */
function availablePositions(available) {
  if (!Array.isArray(available) || available.length === 0) {
    return CONFIG.sizes.map((_, i) => i);
  }
  const positions = available
    .map((size) => CONFIG.sizes.indexOf(size))
    .filter((i) => i !== -1)
    .sort((a, b) => a - b);
  return positions.length ? positions : CONFIG.sizes.map((_, i) => i);
}

/**
 * @typedef {object} Recommendation
 * @property {string} size            Recommended size
 * @property {number} index           Its position in CONFIG.sizes
 * @property {boolean} beyondLargest  The inputs pointed past the biggest size sold, so the
 *   answer is the closest we have rather than a genuine fit. Worth saying out loud.
 */

/**
 * @param {object} input
 * @param {number} input.heightIn
 * @param {number} input.weightLb
 * @param {number} [input.chestIn] Body chest in inches. Optional: most shoppers have not
 *   measured, and the answer must not depend on it.
 * @param {string} input.fit
 * @param {string[]} [input.available] Sizes this product actually sells
 * @returns {Recommendation}
 */
export function recommend({ heightIn, weightLb, chestIn, fit, available }) {
  /**
   * Two anchors, and the larger one wins.
   *
   * They measure different things and disagree honestly: someone lean and broad reads small by
   * weight, someone heavy and narrow reads large. Taking the larger keeps to the rule the whole
   * tool is built on, that a shirt arriving too big still gets worn and one arriving too small
   * is a guaranteed return.
   *
   * The cost is real and worth stating: the weight bands are still unvalidated, so they can only
   * ever push the answer above what the vendor's own chest chart says, never below. Somebody who
   * took the trouble to measure can be handed a bigger size than the chart alone would give.
   * Revisit once there is exchange data to settle the bands.
   */
  const weightIdx = baseIndexFromWeight(weightLb);
  const chestIdx =
    Number.isFinite(chestIn) && chestIn > 0 ? baseIndexFromChest(chestIn) : -1;
  const baseIdx = Math.max(weightIdx, chestIdx);

  const hOffset = heightOffset(heightIn);
  const fOffset = fitOffset(fit);

  // Keep the uncapped figure. Clamping silently would hand somebody a confident "3XL" when
  // their answers actually pointed past it, and 3XL is where the range stops.
  const rawIdx = baseIdx + hOffset + fOffset;
  const targetIdx = clampIndex(rawIdx);

  // Snap to a size the product carries. Availability is a stock question, not a sizing one.
  const positions = availablePositions(available);
  let pos = 0;
  for (let i = 1; i < positions.length; i += 1) {
    if (Math.abs(positions[i] - targetIdx) < Math.abs(positions[pos] - targetIdx)) pos = i;
  }
  const finalIdx = positions[pos];

  return {
    size: CONFIG.sizes[finalIdx],
    index: finalIdx,
    beyondLargest: rawIdx > positions[positions.length - 1],
  };
}

/* ------------------------------------------------------------------ *
 * Pickable ranges.
 *
 * The algorithm only ever distinguished six weight bands and three
 * height bands, so an exact figure was information we collected and
 * immediately discarded. Offering the bands directly costs no accuracy
 * at all, and asks nobody to type their real weight into a shop.
 *
 * Both lists are derived from the thresholds above rather than written
 * out, so an option can never describe a band the logic does not have.
 * ------------------------------------------------------------------ */

/** @param {number} inches @returns {string} e.g. `5'10"` */
export function formatFeetInches(inches) {
  const parts = inchesToFeetIn(inches);
  return `${parts.feet}'${parts.inches}"`;
}

/**
 * @param {'ftin' | 'cm'} [unit]
 * @returns {{ inches: number, label: string }[]} `inches` is a value inside the band, so the
 *   existing height logic keeps working unchanged.
 */
export function heightBandOptions(unit = 'ftin') {
  const [min, max] = CONFIG.limits.heightIn;
  const short = CONFIG.shortInches;
  const tall = CONFIG.tallInches;
  const metric = unit === 'cm';

  // Convert each threshold once and label from those two numbers. Converting both ends of every
  // band separately and rounding each one makes the boundaries disagree: 6'1" rounds down to
  // 185cm while 6'2" rounds up to 188cm, leaving 186 and 187 in no band at all.
  const lower = metric ? Math.round(inchesToCm(short + 1)) : short + 1;
  const upper = metric ? Math.round(inchesToCm(tall)) : tall;
  const withUnit = (value) => (metric ? `${value} cm` : formatFeetInches(value));
  const bare = (value) => (metric ? String(value) : formatFeetInches(value));

  return [
    { inches: Math.round((min + short) / 2), label: `Under ${withUnit(lower)}` },
    { inches: Math.round((short + tall) / 2), label: `${bare(lower)} to ${withUnit(upper - 1)}` },
    { inches: Math.round((tall + max) / 2), label: `${withUnit(upper)} and over` },
  ];
}

/**
 * Labels and representative values for a list of half-open bands.
 *
 * `convert` runs on each edge exactly once and both ends of every label are read back out of
 * that single list. Converting a band's two ends separately and rounding each independently is
 * what once left 186cm and 187cm in no band at all, and had "Under 59 kg" sitting next to
 * "59 to 70 kg".
 *
 * @param {number[]} bands Half-open upper bounds, ascending
 * @param {[number, number]} limits Outer floor and ceiling, for the open first and last bands
 * @param {string} suffix Unit written after every number
 * @param {(edge: number) => number} [convert] Applied once per edge, for display only
 * @returns {{ value: number, label: string }[]} `value` sits inside its band, in source units.
 */
function bandOptions(bands, limits, suffix, convert = (edge) => edge) {
  const [min, max] = limits;
  const edges = [min, ...bands, max + 1];
  const lastIndex = edges.length - 2;
  const shown = edges.map(convert);

  return edges.slice(0, -1).map((lo, i) => {
    let label;
    if (i === 0) label = `Under ${shown[1]} ${suffix}`;
    else if (i === lastIndex) label = `Over ${shown[i] - 1} ${suffix}`;
    else label = `${shown[i]} to ${shown[i + 1] - 1} ${suffix}`;
    return { value: Math.round((lo + edges[i + 1] - 1) / 2), label };
  });
}

/**
 * @param {'lb' | 'kg'} [unit]
 * @returns {{ lb: number, label: string }[]} `lb` is a value inside the band. Always pounds,
 *   whatever the label says, because the bands are defined in pounds.
 */
export function weightBandOptions(unit = 'lb') {
  const metric = unit === 'kg';
  return bandOptions(
    CONFIG.weightBands,
    CONFIG.limits.weightLb,
    metric ? 'kg' : 'lb',
    metric ? (lb) => Math.round(lbToKg(lb)) : undefined
  ).map(({ value, label }) => ({ lb: value, label }));
}

/**
 * @param {'in' | 'cm'} [unit]
 * @returns {{ inches: number, label: string }[]} `inches` is a value inside the band. Always
 *   inches, whatever the label says.
 */
export function chestBandOptions(unit = 'in') {
  const metric = unit === 'cm';
  return bandOptions(
    CONFIG.chestBands,
    CONFIG.limits.chestIn,
    metric ? 'cm' : 'inches',
    metric ? (inches) => Math.round(inchesToCm(inches)) : undefined
  ).map(({ value, label }) => ({ inches: value, label }));
}

/* ------------------------------------------------------------------ *
 * The drawer. Everything below is browser-only.
 * ------------------------------------------------------------------ */

/** Reads and parses a JSON script tag, returning a fallback on any problem. */
function readJson(root, selector, fallback) {
  try {
    const node = root.querySelector(selector);
    return node ? JSON.parse(node.textContent) : fallback;
  } catch {
    return fallback;
  }
}

// Importing this module under Node (for the algorithm tests) must not require a DOM, and a
// class declaration evaluates its `extends` clause at module load.
const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class SizeFinder extends ElementBase {
  #scrollY = 0;
  #locked = false;
  #listeners = null;
  #selectTab = null;

  connectedCallback() {
    this.sizeData = readJson(this, '[data-sizes]', []);
    this.optionName = this.dataset.optionName || 'Size';
    this.autoSelect = this.dataset.autoSelect !== 'false';
    this.units = this.dataset.units === 'metric' ? 'metric' : 'imperial';

    this.dialog = this.querySelector('[data-dialog]');
    this.form = this.querySelector('[data-form]');
    this.resultPane = this.querySelector('[data-result]');
    this.trigger = this.querySelector('[data-open]');

    this.#bind();
    this.#syncUnits();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    // If a section re-render rips this element out while the drawer is open, the close event
    // never arrives, so release the page here rather than leaving it pinned.
    this.#unlockScroll();
  }

  /**
   * Changing a variant makes Horizon re-render the section, which can reconnect this element
   * with fresh inner nodes. Dropping the previous listeners and rebinding is what keeps that
   * safe: binding twice would open the drawer twice per click, and skipping the rebind would
   * leave the handlers attached to nodes that are no longer in the document.
   */
  #bind() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.trigger?.addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-close]')?.addEventListener('click', () => this.close(), { signal });

    // Every input is a select or a radio, so a change is always a complete, valid answer.
    // Nothing to validate, nothing to debounce, nothing to press.
    this.form?.addEventListener('change', () => this.#update(), { signal });

    this.querySelectorAll('[data-units-choice]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          this.units = button.dataset.unitsChoice === 'metric' ? 'metric' : 'imperial';
          this.#syncUnits();
        },
        { signal }
      );
    });

    this.#bindTabs(signal);

    // Backdrop click and the Escape key both surface as `close` on a native dialog.
    this.dialog?.addEventListener('close', () => this.#afterClose(), { signal });
    this.dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.dialog.close();
      },
      { signal }
    );
  }

  /* -------- tabs -------- */

  /**
   * Standard tab pattern: arrows move between tabs, only the selected one is in the tab order.
   * No-ops when the chart is switched off, because then there is only ever one panel and the
   * markup carries none of the tab roles.
   *
   * @param {AbortSignal} signal
   */
  #bindTabs(signal) {
    this.#selectTab = null;
    const tabs = Array.from(this.querySelectorAll('[data-tab]'));
    if (tabs.length < 2) return;
    const panels = Array.from(this.querySelectorAll('[data-panel]'));

    const select = (index, moveFocus) => {
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        // Roving tabindex: Tab reaches the tablist once, then arrows move within it.
        tab.tabIndex = active ? 0 : -1;
        panels[i]?.toggleAttribute('hidden', !active);
      });
      if (moveFocus) tabs[index].focus();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i, false), { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          const last = tabs.length - 1;
          let next = null;
          if (event.key === 'ArrowRight') next = i === last ? 0 : i + 1;
          else if (event.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = last;
          if (next === null) return;
          event.preventDefault();
          select(next, true);
        },
        { signal }
      );
    });

    this.#selectTab = select;
  }

  /* -------- open / close -------- */

  open() {
    if (!this.dialog) return;
    this.#lockScroll();
    // Always open on the questions. The trigger promises to find a size, so landing on a
    // measurement table because that is where the last visit ended would be a bait and switch.
    this.#selectTab?.(0, false);
    this.dialog.showModal();
  }

  close() {
    this.dialog?.close();
  }

  #afterClose() {
    this.#unlockScroll();
    this.trigger?.focus();
  }

  /**
   * Horizon locks scrolling with a `scroll-lock` attribute on <html>, which base.css turns into
   * `overflow: hidden` on both <html> and `.page-wrapper`.
   *
   * Using it matters: above 990px `.page-wrapper` is the scroll container, not <body>, so
   * setting overflow on <body> locked nothing and the page kept scrolling behind the open
   * drawer. Below 990px the page scrolls on <html>, which the same attribute covers.
   *
   * The attribute is set directly rather than importing the theme's `lockScroll()` helper,
   * because this module is also imported by the Node tests where a bare `@theme/*` specifier
   * cannot resolve. Safe here because the drawer is modal: nothing else can take or release a
   * lock while it is open.
   *
   * Both halves are idempotent. `close` fires in a queued task, so an open can land while a
   * previous unlock is still pending, and without the guard the lock could be left stuck on.
   */
  #lockScroll() {
    if (this.#locked) return;
    this.#locked = true;
    this.#scrollY = window.scrollY;
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #unlockScroll() {
    if (!this.#locked) return;
    this.#locked = false;
    document.documentElement.removeAttribute('scroll-lock');
    if (Math.abs(window.scrollY - this.#scrollY) > 1) window.scrollTo(0, this.#scrollY);
  }

  /* -------- units -------- */

  /**
   * Fills both range selects for the current unit system. The stored values are always
   * imperial, so switching units only relabels and the current choice survives.
   */
  #renderBands() {
    const heightSelect = this.querySelector('[data-height-band]');
    const weightSelect = this.querySelector('[data-weight-band]');
    if (!heightSelect || !weightSelect) return;

    const metric = this.units === 'metric';
    const keptHeight = heightSelect.value;
    const keptWeight = weightSelect.value;

    const option = (value, label) => {
      const node = document.createElement('option');
      node.value = String(value);
      node.textContent = label;
      return node;
    };

    // Prompts from the closed select, but `hidden` keeps it out of the opened list, where a
    // greyed "Your height" sitting above the real ranges reads like a stray entry.
    const prompt = (label) => {
      const node = option('', label);
      node.disabled = true;
      node.hidden = true;
      return node;
    };

    // Height starts unchosen for the same reason weight does. It used to open on the middle band,
    // which looks like an answer the shopper gave rather than one nobody has given yet, and the
    // one most likely to be left alone is the one nearest the middle of the range.
    heightSelect.replaceChildren(
      prompt('Your height'),
      ...heightBandOptions(metric ? 'cm' : 'ftin').map((band) => option(band.inches, band.label))
    );
    heightSelect.value = keptHeight || '';

    weightSelect.replaceChildren(
      prompt('Your weight'),
      ...weightBandOptions(metric ? 'kg' : 'lb').map((band) => option(band.lb, band.label))
    );
    weightSelect.value = keptWeight || '';

    const chestSelect = this.querySelector('[data-chest-band]');
    if (chestSelect) {
      const keptChest = chestSelect.value;
      // Asks for the measurement rather than offering a way out of it, so the empty state reads
      // as something still to do. Selectable, unlike the two prompts above, because chest is
      // optional and anyone who picked a band by accident has to be able to put it back.
      chestSelect.replaceChildren(
        option('', 'Your chest'),
        ...chestBandOptions(metric ? 'cm' : 'in').map((band) => option(band.inches, band.label))
      );
      chestSelect.value = keptChest || '';
    }
  }

  #syncUnits() {
    this.querySelectorAll('[data-units-choice]').forEach((button) => {
      const pressed = (button.dataset.unitsChoice === 'metric') === (this.units === 'metric');
      button.setAttribute('aria-pressed', String(pressed));
    });
    this.#renderBands();
    this.#update();
  }

  /* -------- result -------- */

  /**
   * Recalculates from the current selections. Runs on every change, so it must be safe to call
   * constantly and must never move focus.
   */
  #update() {
    const heightIn = Number(this.querySelector('[data-height-band]')?.value);
    const weightLb = Number(this.querySelector('[data-weight-band]')?.value);

    // Both start unchosen. No answer until they are picked, because guessing either would be a
    // claim we have not earned.
    if (!Number.isFinite(heightIn) || !heightIn || !weightLb) {
      this.resultPane?.setAttribute('hidden', '');
      return;
    }

    const fit = this.querySelector('[name$="-fit"]:checked')?.value ?? 'designed';

    // Blank reads as 0 and a missing select as NaN. recommend() treats both as no answer, which
    // is the whole point of chest being optional.
    const chestIn = Number(this.querySelector('[data-chest-band]')?.value);

    // Every size the product offers, in stock or not. A sold-out L is still the right answer
    // for someone who wears an L. Stock decides whether we can select it, not what fits.
    this.#render(
      recommend({
        heightIn,
        weightLb,
        chestIn,
        fit,
        available: this.sizeData.map((entry) => entry.value),
      })
    );
  }

  /** @param {Recommendation} result */
  #render(result) {
    const wasHidden = this.resultPane?.hasAttribute('hidden');

    // Reveal the pane before writing into it. A live region that is still hidden when its
    // content changes does not reliably announce.
    this.resultPane?.removeAttribute('hidden');

    this.querySelector('[data-result-size]').textContent = result.size;

    const chooseNote = this.querySelector('[data-choose-note]');
    if (chooseNote) chooseNote.textContent = '';

    const limitNote = this.querySelector('[data-limit-note]');
    if (limitNote) {
      limitNote.textContent = result.beyondLargest
        ? CONFIG.messages.beyondLargest.replace('{size}', result.size)
        : '';
    }

    const choose = this.querySelector('[data-choose]');
    if (choose) {
      choose.textContent = this.autoSelect ? `Choose size ${result.size}` : 'Got it';
      choose.onclick = () => this.#choose(result.size);
    }

    // Only when it first appears. Scrolling on every keystroke would fight the shopper while
    // they type, and focus is deliberately left alone for the same reason. The live region
    // handles the announcement.
    if (wasHidden) this.resultPane?.scrollIntoView({ block: 'nearest' });
  }

  /* -------- variant selection -------- */

  #choose(size) {
    if (!this.autoSelect) {
      this.close();
      return;
    }
    if (this.#selectVariant(size) === 'ok') {
      this.close();
      return;
    }

    // Never leave the shopper stuck: keep the recommendation on screen and point at the swatches.
    const note = this.querySelector('[data-choose-note]');
    if (note) note.textContent = CONFIG.messages.selectFailed;
  }

  /**
   * Horizon's variant picker renders real light-DOM radios (or a select, depending on the
   * block setting) and listens for a bubbling `change`. Dispatching that event is the
   * supported path. Faking clicks is not.
   *
   * @param {string} size
   * @returns {'ok' | 'unavailable' | 'missing'}
   */
  #selectVariant(size) {
    const picker =
      document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector('variant-picker');
    if (!picker) return 'missing';

    const matches = (node) => node.dataset.optionName === this.optionName && node.value === size;

    const radio = Array.from(picker.querySelectorAll('input[type="radio"][data-option-name]')).find(
      matches
    );
    // A sold-out size is still selectable in Horizon. The swatch takes the selection and the
    // page shows the sold-out state. Mirror that instead of refusing, so the shopper can see
    // what happened to the size we recommended.
    if (radio) {
      if (radio.checked) return 'ok';
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return 'ok';
    }

    const option = Array.from(picker.querySelectorAll('option[data-option-name]')).find(matches);
    const select = option?.closest('select');
    if (!select) return 'missing';
    if (select.value === size) return 'ok';
    select.value = size;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return 'ok';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('size-finder')) {
  customElements.define('size-finder', SizeFinder);
}
